
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Analytics } from "@vercel/analytics/react";
import logooasis from "./asset/logooasis.png";
import FeaturesSection from './components/ui/features-section';
import HowItWorks from './components/HowItWorks';
import OasisNavbar from './components/OasisNavbar';
// Icons
import {
  IoSearchOutline,
  IoMedicalOutline,
  IoBookOutline,
  IoRestaurantOutline,
  IoShieldCheckmarkOutline,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoMapOutline,
  IoLanguageOutline,
  IoEarthOutline,
  IoLockClosedOutline,
  IoLocationOutline,
  IoFlashOutline,
  IoPhonePortraitOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoGlobeOutline
} from 'react-icons/io5';
import { FaTooth } from 'react-icons/fa';

const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState<boolean>(true);

  // Screenshots data
  const screenshots = [
    { src: "/screenshots/category-selection.png", alt: "Service Categories" },
    { src: "/screenshots/map-view.png", alt: "Map View" },
    { src: "/screenshots/filter-options.png", alt: "Filter Options" }
  ];

  // Scroll animation observer
  React.useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, observerOptions);

    // Observe all elements with scroll-animate classes
    const animatedElements = document.querySelectorAll(
      '.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Carousel auto-play (solo cuando isAutoPlaying es true)
  React.useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % screenshots.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, screenshots.length]);

  // Reanudar auto-play después de 8 segundos de inactividad
  React.useEffect(() => {
    if (isAutoPlaying) return;

    const timeout = setTimeout(() => {
      setIsAutoPlaying(true);
    }, 8000); // Resume auto-play after 8 seconds

    return () => clearTimeout(timeout);
  }, [isAutoPlaying, currentSlide]);

  // Carousel navigation functions
  const nextSlide = () => {
    setIsAutoPlaying(false); // Pausar auto-play cuando el usuario interactúa
    setCurrentSlide((prev) => (prev + 1) % screenshots.length);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false); // Pausar auto-play cuando el usuario interactúa
    setCurrentSlide((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false); // Pausar auto-play cuando el usuario interactúa
    setCurrentSlide(index);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="min-h-screen bg-cream overflow-x-hidden">
      {/* Navigation */}
      <OasisNavbar />

      {/* Hero Section */}
      <section
        id="home"
        className="relative min-h-screen bg-gradient-to-br from-primary-green via-ocean-blue to-secondary-blue flex items-center pt-20 lg:pt-0 overflow-hidden"
      >
        {/* Animated Background Blobs */}
        <div className="absolute top-[10%] right-[8%] w-64 h-64 lg:w-80 lg:h-80 bg-secondary-blue/40 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-[-8%] left-[-10%] w-80 h-80 lg:w-96 lg:h-96 bg-primary-green/40 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left space-y-6 lg:space-y-8 px-2 sm:px-0">
              <h1 className="hero-title text-[1.75rem] sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
                {t('hero.title')}{" "}
                <span className="bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                  {t('hero.titleHighlight')}
                </span>
              </h1>

              <p className="hero-subtitle text-[0.9375rem] sm:text-base md:text-lg lg:text-xl text-white/95 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                {t('hero.subtitle')}
              </p>

              {/* CTA Buttons - Google Play & App Store */}
              <div className="hero-cta flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                <a
                  href="https://play.google.com/store/apps/details?id=com.oasis.oakland"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group px-8 py-4 bg-white text-black font-bold rounded-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 focus:outline-none focus:ring-4 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent"
                  aria-label={t('hero.googlePlayLabel') + ' - ' + t('hero.googlePlayStore')}
                >
                  <img
                    src="/Google Play Icons.png"
                    alt="Google Play"
                    className="w-8 h-8 group-hover:scale-110 transition-transform"
                  />
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] sm:text-xs uppercase tracking-wide text-black/70">{t('hero.googlePlayLabel')}</span>
                    <span className="text-base sm:text-lg font-bold -mt-0.5">{t('hero.googlePlayStore')}</span>
                  </div>
                </a>
                <a
                  href="https://apps.apple.com/us/app/oasis-oakland/id6755713254"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group px-8 py-4 bg-white text-black font-bold rounded-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 focus:outline-none focus:ring-4 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent"
                  aria-label={t('hero.googlePlayLabel') + ' - App Store'}
                >
                  <svg className="w-8 h-8 group-hover:scale-110 transition-transform" viewBox="0 0 384 512" fill="black">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                  </svg>
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] sm:text-xs uppercase tracking-wide text-black/70">{t('hero.googlePlayLabel')}</span>
                    <span className="text-base sm:text-lg font-bold -mt-0.5">App Store</span>
                  </div>
                </a>
              </div>

              {/* Unified Badge - Responsive */}
              <div className="hero-badge flex justify-center lg:justify-start px-4 sm:px-0">
                <div className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 bg-white/15 backdrop-blur-md border border-white/30 rounded-full text-white font-medium text-xs sm:text-sm shadow-lg max-w-full">
                  <div className="flex items-center gap-1.5">
                    <IoCheckmarkCircleOutline className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">{t('hero.badgeFree')}</span>
                  </div>
                  <span className="text-white/40 hidden sm:inline">•</span>
                  <div className="flex items-center gap-1.5">
                    <IoLockClosedOutline className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">{t('hero.badgeNoLogin')}</span>
                  </div>
                  <span className="text-white/40 hidden sm:inline">•</span>
                  <div className="flex items-center gap-1.5">
                    <IoEarthOutline className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">{t('hero.badgeLanguages')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Visual - App Screenshots Carousel */}
            <div className="hero-image flex justify-center lg:justify-end">
              <div className="relative w-full max-w-sm lg:max-w-md px-4 lg:px-0">
                {/* Carousel Container */}
                <div className="relative">
                  {/* Screenshots */}
                  <div className="relative h-[520px] sm:h-[580px] flex items-center justify-center">
                    {screenshots.map((screenshot, index) => (
                      <div
                        key={index}
                        className={`absolute inset - 0 transition - all duration - 700 ease -in -out ${index === currentSlide
                          ? 'opacity-100 translate-x-0 scale-100'
                          : index < currentSlide
                            ? 'opacity-0 -translate-x-full scale-95'
                            : 'opacity-0 translate-x-full scale-95'
                          } `}
                      >
                        <div className="flex items-center justify-center h-full">
                          <img
                            src={screenshot.src}
                            alt={screenshot.alt}
                            className="w-full max-w-[260px] sm:max-w-[300px] rounded-3xl shadow-2xl object-contain transform hover:scale-105 transition-transform duration-300"
                            style={{
                              filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3))'
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Navigation Buttons */}
                  <button
                    onClick={prevSlide}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/95 hover:bg-white backdrop-blur-md rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 group"
                    aria-label="Previous slide"
                  >
                    <IoChevronBackOutline className="w-5 h-5 sm:w-6 sm:h-6 text-primary-green group-hover:text-primary-green-dark transition-colors" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/95 hover:bg-white backdrop-blur-md rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 group"
                    aria-label="Next slide"
                  >
                    <IoChevronForwardOutline className="w-5 h-5 sm:w-6 sm:h-6 text-primary-green group-hover:text-primary-green-dark transition-colors" />
                  </button>
                </div>

                {/* Carousel Indicators */}
                <div className="flex justify-center gap-2.5 mt-8">
                  {screenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`transition - all duration - 300 rounded - full ${index === currentSlide
                        ? 'w-10 h-3 bg-white shadow-lg'
                        : 'w-3 h-3 bg-white/60 hover:bg-white/80 hover:scale-125'
                        } `}
                      aria-label={`Go to slide ${index + 1} `}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Oasis Oakland? - Features Section */}
      <section id="why" className="bg-gray-50 relative overflow-hidden">
        <FeaturesSection />
      </section>

      {/* Services Preview - Bento Grid */}
      <section id="services" className="py-24 lg:py-32 bg-gray-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="scroll-animate text-4xl lg:text-5xl font-bold text-navy mb-6 tracking-tight">
              {t('services.title')}
            </h2>
            <p className="scroll-animate delay-100 text-xl text-gray-600 leading-relaxed">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 auto-rows-[minmax(200px,auto)]">
            {/* Health - Large Featured Item */}
            <div className="scroll-animate delay-200 md:col-span-2 md:row-span-2 group relative bg-white rounded-[2.5rem] p-8 lg:p-12 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100">
              <div className="absolute top-0 right-0 w-64 h-64 bg-health-red/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 transition-transform duration-700 group-hover:scale-125" />
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                  <div className="w-16 h-16 bg-health-red/10 rounded-2xl flex items-center justify-center mb-6 text-health-red">
                    <IoMedicalOutline className="w-8 h-8" />
                  </div>
                  <h3 className="text-3xl font-bold text-navy mb-4 group-hover:text-health-red transition-colors">
                    {t('services.health.title')}
                  </h3>
                  <p className="text-lg text-gray-600 mb-8 max-w-md">
                    Comprehensive health resources including clinics, mental health support, and wellness programs available to everyone.
                  </p>
                </div>
                <ul className="grid sm:grid-cols-2 gap-4">
                  <li className="flex items-center gap-3 text-gray-700 font-medium">
                    <IoCheckmarkCircleOutline className="w-6 h-6 text-health-red flex-shrink-0" />
                    <span>{t('services.health.item1')}</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700 font-medium">
                    <IoCheckmarkCircleOutline className="w-6 h-6 text-health-red flex-shrink-0" />
                    <span>{t('services.health.item2')}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Dental - Tall Item */}
            <div className="scroll-animate delay-300 md:row-span-2 group relative bg-gradient-to-br from-dental-blue to-dental-blue-dark rounded-[2.5rem] p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden text-white">
              <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-2xl translate-y-1/4 translate-x-1/4" />
              <div className="relative z-10 h-full flex flex-col">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                  <FaTooth className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{t('services.dental.title')}</h3>
                <p className="text-white/90 mb-auto leading-relaxed">
                  Find low-cost and free dental care clinics near you.
                </p>
                <ul className="space-y-4 mt-8">
                  <li className="flex items-start gap-3 text-white/90">
                    <div className="w-1.5 h-1.5 rounded-full bg-white mt-2.5 flex-shrink-0" />
                    <span>{t('services.dental.item1')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/90">
                    <div className="w-1.5 h-1.5 rounded-full bg-white mt-2.5 flex-shrink-0" />
                    <span>{t('services.dental.item2')}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Literacy */}
            <div className="scroll-animate delay-400 group relative bg-white rounded-[2rem] p-8 shadow-md hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-literacy-green/30">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-literacy-green/10 rounded-xl flex items-center justify-center text-literacy-green">
                  <IoBookOutline className="w-6 h-6" />
                </div>
                <IoChevronForwardOutline className="w-5 h-5 text-gray-300 group-hover:text-literacy-green transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2 group-hover:text-literacy-green transition-colors">{t('services.literacy.title')}</h3>
              <p className="text-sm text-gray-600 line-clamp-2">{t('services.literacy.item1')}</p>
            </div>

            {/* Food */}
            <div className="scroll-animate delay-500 group relative bg-white rounded-[2rem] p-8 shadow-md hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-food-orange/30">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-food-orange/10 rounded-xl flex items-center justify-center text-food-orange">
                  <IoRestaurantOutline className="w-6 h-6" />
                </div>
                <IoChevronForwardOutline className="w-5 h-5 text-gray-300 group-hover:text-food-orange transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2 group-hover:text-food-orange transition-colors">{t('services.food.title')}</h3>
              <p className="text-sm text-gray-600 line-clamp-2">{t('services.food.item1')}</p>
            </div>

            {/* Insurance */}
            <div className="scroll-animate delay-600 group relative bg-white rounded-[2rem] p-8 shadow-md hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-insurance-purple/30">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-insurance-purple/10 rounded-xl flex items-center justify-center text-insurance-purple">
                  <IoShieldCheckmarkOutline className="w-6 h-6" />
                </div>
                <IoChevronForwardOutline className="w-5 h-5 text-gray-300 group-hover:text-insurance-purple transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2 group-hover:text-insurance-purple transition-colors">{t('services.insurance.title')}</h3>
              <p className="text-sm text-gray-600 line-clamp-2">{t('services.insurance.item1')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Vertical Timeline */}
      <HowItWorks />

      {/* Who We Serve - Split Layout */}
      <section id="who" className="py-24 lg:py-32 bg-navy text-white relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-green/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-secondary-blue/10 rounded-full blur-3xl -translate-x-1/3 translate-y-1/4" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="scroll-animate">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-8 backdrop-blur-sm">
                <IoLocationOutline className="w-5 h-5 text-primary-green" />
                <span className="font-medium text-sm tracking-wide">{t('who.badge')}</span>
              </div>
              <h2 className="text-4xl lg:text-6xl font-bold mb-8 leading-tight">
                {t('who.title')}
              </h2>
              <p className="text-xl text-white/80 leading-relaxed mb-12 max-w-xl">
                {t('who.subtitle')}
              </p>

              <div className="p-8 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-sm">
                <p className="text-lg font-medium leading-relaxed">
                  "{t('who.note')}"
                </p>
              </div>
            </div>

            <div className="scroll-animate delay-200">
              <div className="space-y-6">
                {[
                  t('who.item1'),
                  t('who.item2'),
                  t('who.item3'),
                  t('who.item4'),
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-6 p-6 bg-white/5 hover:bg-white/10 rounded-2xl transition-colors border border-white/5 group">
                    <div className="w-12 h-12 rounded-full bg-primary-green flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      <IoCheckmarkCircleOutline className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xl font-medium text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Privacy Policy Section */}
      <section id="privacy" className="py-24 lg:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-navy mb-6">
              {t('privacy.title')}
            </h2>
          </div>

          {/* Privacy Highlights - Icon Row */}
          <div className="flex flex-col md:flex-row justify-center items-start gap-12 mb-20 pb-12 border-b border-gray-100">
            <div className="flex-1 text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-green/10 rounded-full mb-6 group-hover:scale-110 transition-transform text-primary-green">
                <IoSearchOutline className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3">{t('privacy.transparent')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('privacy.transparentDesc')}</p>
            </div>
            <div className="hidden md:block w-px h-32 bg-gray-100 self-center" />
            <div className="flex-1 text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary-blue/10 rounded-full mb-6 group-hover:scale-110 transition-transform text-secondary-blue">
                <IoPhonePortraitOutline className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3">{t('privacy.localFirst')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('privacy.localFirstDesc')}</p>
            </div>
            <div className="hidden md:block w-px h-32 bg-gray-100 self-center" />
            <div className="flex-1 text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-ocean-blue/10 rounded-full mb-6 group-hover:scale-110 transition-transform text-ocean-blue">
                <IoFlashOutline className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3">{t('privacy.empowering')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('privacy.empoweringDesc')}</p>
            </div>
          </div>

          {/* Privacy Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-cream p-8 rounded-2xl mb-8">
              <p className="text-gray-700 leading-relaxed">
                {t('privacy.intro')}
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-navy mb-4">{t('privacy.section1.title')}</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                {t('privacy.section1.intro')}
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span><strong>{t('privacy.section1.personal')}</strong> {t('privacy.section1.personalDesc')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span><strong>{t('privacy.section1.location')}</strong> {t('privacy.section1.locationDesc')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span><strong>{t('privacy.section1.saved')}</strong> {t('privacy.section1.savedDesc')}</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 leading-relaxed">
                {t('privacy.section1.outro')}
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-navy mb-4">{t('privacy.section2.title')}</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">{t('privacy.section2.intro')}</p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span>{t('privacy.section2.item1')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span>{t('privacy.section2.item2')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span>{t('privacy.section2.item3')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span>{t('privacy.section2.item4')}</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 leading-relaxed">
                {t('privacy.section2.outro')}
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-navy mb-4">{t('privacy.section3.title')}</h3>
              <p className="text-gray-700 leading-relaxed">
                {t('privacy.section3.text')}
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-navy mb-4">{t('privacy.section4.title')}</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">{t('privacy.section4.intro')}</p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span>{t('privacy.section4.item1')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span>{t('privacy.section4.item2')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span>{t('privacy.section4.item3')}</span>
                </li>
              </ul>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-navy mb-4">{t('privacy.section5.title')}</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">{t('privacy.section5.intro')}</p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span><strong>{t('privacy.section5.access')}</strong> {t('privacy.section5.accessDesc')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span><strong>{t('privacy.section5.withdraw')}</strong> {t('privacy.section5.withdrawDesc')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary-green font-bold mt-1">•</span>
                  <span><strong>{t('privacy.section5.deletion')}</strong> {t('privacy.section5.deletionDesc')}</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 leading-relaxed">{t('privacy.section5.outro')}</p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-navy mb-4">{t('privacy.section6.title')}</h3>
              <p className="text-gray-700 leading-relaxed">
                {t('privacy.section6.text')}
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-navy mb-4">{t('privacy.section7.title')}</h3>
              <p className="text-gray-700 leading-relaxed">
                {t('privacy.section7.text')}
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-navy mb-4">{t('privacy.section8.title')}</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                {t('privacy.section8.intro')}
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                {t('privacy.section8.details')}
              </p>
              <div className="bg-secondary-blue/5 p-6 rounded-xl border-l-4 border-secondary-blue">
                <p className="text-gray-700 font-semibold">
                  {t('privacy.section8.responsibility')}
                </p>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-navy mb-4">{t('privacy.section9.title')}</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                {t('privacy.section9.intro')}
              </p>
              <div className="bg-primary-green/5 p-6 rounded-xl">
                <p className="text-gray-700">
                  <strong>{t('privacy.section9.email')}</strong> teamoasisoakland@gmail.com
                </p>
              </div>
            </div>

            <div className="bg-navy/5 p-6 rounded-xl">
              <p className="text-gray-700 italic">
                {t('privacy.agreement')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy text-white py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex justify-center md:justify-start mb-6">
                <img
                  src={logooasis}
                  alt="Oasis Oakland"
                  className="h-16 w-16 rounded-2xl bg-white p-2 shadow-lg"
                />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-center md:text-left">Oasis Oakland</h3>
              <p className="text-white/70 text-center md:text-left leading-relaxed">
                {t('footer.tagline')}
              </p>
            </div>

            {/* About */}
            <div>
              <h4 className="font-bold text-lg mb-6 text-primary-green">{t('footer.about')}</h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href="#why"
                    className="text-white/70 hover:text-white transition-colors hover:translate-x-1 inline-block"
                  >
                    {t('footer.mission')}
                  </a>
                </li>
                <li>
                  <a
                    href="#services"
                    className="text-white/70 hover:text-white transition-colors hover:translate-x-1 inline-block"
                  >
                    {t('nav.services')}
                  </a>
                </li>
                <li>
                  <a
                    href="#who"
                    className="text-white/70 hover:text-white transition-colors hover:translate-x-1 inline-block"
                  >
                    {t('footer.whoWeServe')}
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-bold text-lg mb-6 text-secondary-blue">{t('footer.contact')}</h4>
              <ul className="space-y-4 text-white/70">
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary-blue"></span>
                  teamoasisoakland@gmail.com
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary-blue"></span>
                  Oakland, CA
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-bold text-lg mb-6 text-ocean-blue">{t('footer.legal')}</h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href="#privacy"
                    className="text-white/70 hover:text-white transition-colors hover:translate-x-1 inline-block"
                  >
                    {t('footer.privacyPolicy')}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/50 mb-2 text-sm">
              {t('footer.copyright')}
            </p>
            <p className="text-white/50 text-sm">
              {t('footer.madeWith')}
            </p>
          </div>
        </div>
      </footer>



      {/* Vercel Analytics */}
      <Analytics />
    </div>
  );
};

export default App;
